import { useState, useEffect } from 'react';
import { TabState } from '../types';
import { compareWeek } from '../utils/scheduleHelpers';

/**
 * Custom hook for managing tab state with intelligent default based on week comparison
 */
export const useTabState = (
  targetWeek?: { period: number; week: number; year: number },
  currentWeek?: { period: number; week: number; year: number }
) => {
  // Calculate intelligent default tab based on week comparison per truth table
  const getDefaultTab = (): 'schedule' | 'actual' => {
    if (!targetWeek || !currentWeek) {
      return 'schedule'; // Default fallback
    }

    // Only proceed if we have valid period and week data
    if (targetWeek.period <= 0 || targetWeek.week <= 0 || currentWeek.period <= 0 || currentWeek.week <= 0) {
      return 'schedule'; // Default fallback for invalid data
    }

    const weekComparison = compareWeek(targetWeek, currentWeek);
    
    // Truth Table Implementation:
    // Past Week (comparison < 0): Default to Schedule tab
    // Current Week (comparison = 0): Default to Actual tab  
    // Future Week (comparison > 0): Default to Schedule tab
    if (weekComparison < 0) return 'schedule';  // Past - default to Schedule
    if (weekComparison === 0) return 'actual';  // Current - default to Actual
    if (weekComparison > 0) return 'schedule';  // Future - default to Schedule
    
    return 'schedule'; // Fallback
  };

  const [activeTab, setActiveTab] = useState<'schedule' | 'actual'>(getDefaultTab());

  // Update default tab when week data changes
  useEffect(() => {
    if (targetWeek && currentWeek) {
      // Only update if we have valid period and week data
      if (targetWeek.period > 0 && targetWeek.week > 0 && currentWeek.period > 0 && currentWeek.week > 0) {
        const defaultTab = getDefaultTab();
        setActiveTab(defaultTab);
        
        console.log('=== Tab State Update ===');
        console.log('targetWeek:', targetWeek);
        console.log('currentWeek:', currentWeek);
        console.log('weekComparison:', compareWeek(targetWeek, currentWeek));
        console.log('defaultTab set to:', defaultTab);
      }
    }
  }, [targetWeek?.period, targetWeek?.week, targetWeek?.year, currentWeek?.period, currentWeek?.week, currentWeek?.year]);

  const tabState: TabState = {
    activeTab
  };

  return {
    tabState,
    setActiveTab
  };
};